import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import BrandMark from '../components/BrandMark';
import { authApi } from '../api/authApi';
import { useAuth } from '../context/AuthContext';
import CaptchaGate, { HoneypotField } from '../components/CaptchaGate';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Two-factor prompt state. Server returns TWO_FACTOR_REQUIRED
  // on the first submit if 2FA is on; we flip to a code prompt
  // and read err.details to know what channel + which masked
  // target to display.
  const [twoFactorMode, setTwoFactorMode] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState('totp');
  const [twoFactorTarget, setTwoFactorTarget] = useState('');
  const [resending, setResending] = useState(false);

  const [captchaToken, setCaptchaToken] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const tf = twoFactorMode
        ? (useRecovery ? { recoveryCode: twoFactorCode } : { twoFactorCode })
        : {};
      // Bot-protection fields ride along; stripped server-side.
      await login(email.trim(), password, { ...tf, captchaToken, nickname: honeypot });
      const dest = location.state?.from || '/app';
      navigate(dest, { replace: true });
    } catch (err) {
      if (err.code === 'TWO_FACTOR_REQUIRED' && !twoFactorMode) {
        setTwoFactorMethod(err.details?.method || 'totp');
        setTwoFactorTarget(err.details?.maskedTarget || '');
        setTwoFactorMode(true);
        setBusy(false);
        return;
      }
      setError(err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (resending) return;
    setResending(true);
    setError('');
    try {
      const result = await authApi.resendTwoFactorChallenge(email.trim(), password);
      if (result?.maskedTarget) setTwoFactorTarget(result.maskedTarget);
    } catch (err) {
      setError(err.message || 'Could not resend');
    } finally {
      setResending(false);
    }
  };

  const cancelTwoFactor = () => {
    setTwoFactorMode(false);
    setTwoFactorCode('');
    setUseRecovery(false);
    setTwoFactorMethod('totp');
    setTwoFactorTarget('');
    setError('');
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <Link to="/" style={{ marginBottom: 24, display: 'inline-block' }}><BrandMark size={28} /></Link>
        <h2 className="h2">Welcome back.</h2>
        <p className="muted" style={{ marginBottom: 18 }}>Sign in to keep climbing.</p>

        <form onSubmit={submit}>
          {twoFactorMode ? (
            <>
              <p className="muted" style={{ marginTop: 0 }}>
                {useRecovery
                  ? 'Enter one of the recovery codes you saved when enabling two-factor.'
                  : twoFactorMethod === 'totp'
                    ? 'Open your authenticator app and enter the 6-digit code for Outglow.'
                    : twoFactorMethod === 'sms'
                      ? `We texted a 6-digit code to ${twoFactorTarget || 'your phone'}. Enter it below.`
                      : `We emailed a 6-digit code to ${twoFactorTarget || 'your inbox'}. Enter it below.`}
              </p>
              <div className="form-row">
                <label className="label">{useRecovery ? 'Recovery code' : '6-digit code'}</label>
                <input
                  autoFocus
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  placeholder={useRecovery ? 'ABCDE-FGHIJ' : '••••••'}
                  maxLength={useRecovery ? 16 : 8}
                  required
                />
              </div>
              {error ? <p className="error-text">{error}</p> : null}
              <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy || !twoFactorCode.trim()}>
                {busy ? 'Verifying…' : 'Verify and sign in →'}
              </button>
              <div className="row" style={{ justifyContent: 'space-between', marginTop: 16, gap: 12, flexWrap: 'wrap' }}>
                {!useRecovery && (twoFactorMethod === 'email' || twoFactorMethod === 'sms') ? (
                  <button type="button" className="muted" onClick={resendCode} disabled={resending} style={{ background: 'none', border: 0, padding: 0, fontSize: 13, cursor: 'pointer' }}>
                    {resending ? 'Sending…' : 'Resend code'}
                  </button>
                ) : <span />}
                <button type="button" className="muted" onClick={() => { setUseRecovery((r) => !r); setTwoFactorCode(''); }} style={{ background: 'none', border: 0, padding: 0, fontSize: 13, cursor: 'pointer' }}>
                  {useRecovery ? 'Use verification code instead' : 'Use a recovery code instead'}
                </button>
                <button type="button" className="muted" onClick={cancelTwoFactor} style={{ background: 'none', border: 0, padding: 0, fontSize: 13, cursor: 'pointer' }}>
                  ← Back
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="form-row">
                <label className="label">Email</label>
                <input
                  type="email" autoComplete="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@domain.com"
                />
              </div>
              <div className="form-row">
                <label className="label">Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{ paddingRight: 64 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    style={{
                      position: 'absolute', right: 10, top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: 12, fontWeight: 800, color: 'var(--muted)',
                      padding: '4px 8px'
                    }}
                  >{showPassword ? 'Hide' : 'Show'}</button>
                </div>
              </div>
              {error ? <p className="error-text">{error}</p> : null}
              <HoneypotField value={honeypot} onChange={setHoneypot} />
              <CaptchaGate onToken={setCaptchaToken} />
              <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in →'}
              </button>
            </>
          )}
        </form>

        <div className="row" style={{ justifyContent: 'space-between', marginTop: 16 }}>
          <Link to="/forgot-password" style={{ fontSize: 13 }}>Forgot password?</Link>
          <Link to="/register" style={{ fontSize: 13 }}>Create account</Link>
        </div>
      </div>

      <style>{`
        .auth-shell {
          min-height: 100vh;
          display: grid; place-items: center;
          padding: 32px 16px;
          background:
            radial-gradient(circle at 0% 0%, rgba(255,62,127,.16), transparent 35%),
            radial-gradient(circle at 100% 100%, rgba(91,233,255,.10), transparent 40%),
            var(--bg);
        }
        .auth-card {
          width: min(440px, 100%);
          background: var(--panel);
          border: 1px solid var(--line);
          border-radius: var(--radius-lg);
          padding: 28px;
          box-shadow: var(--shadow-card);
        }
      `}</style>
    </div>
  );
}
